import { useRef, useState, type MouseEvent } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAccessControl } from '@plerionhq/design-system';
import { Select } from '@plerionhq/design-system/experimental';
import {
  useUpdateGrant,
  type UpdateGrantBody,
} from '../../api/use-update-grant';
import {
  decisionOptions,
  decisionOptionsFor,
  isReviewLaterWithoutDate,
  toDecision,
} from './decision-options';
import { ReviewDatePopover } from './review-date-popover';
import {
  defaultNextReviewDateInput,
  formatReviewDate,
  fromDateInput,
  isReviewOverdue,
  trustWindowBounds,
} from '../../lib/review-date';
import type { AccessGrant } from '../../types';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// Rows open the slide-over on click; interacting with an editable cell must
// not bubble up and trigger that navigation.
function stopRowClick(event: MouseEvent) {
  event.stopPropagation();
}

// Brief "saved" tick after a successful write, so an inline edit gives
// feedback without a toast on the happy path.
function useSaveIndicator() {
  const [saved, setSaved] = useState(false);
  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  return { saved, flash };
}

/*
 * Width floor for the Decision cell.
 *
 * The design-system Select gives its trigger no intrinsic width to speak of: the
 * value sits in a `min-w-0 flex-1 truncate` box, and the trigger only ever
 * renders the row's CURRENT decision. So nothing in the cell asks for room for
 * the longest option — squeeze the table and the label is clipped to a single
 * letter. TableV2 ignores column `minSize` on this non-virtualized table, so the
 * floor has to live on the cell content (same reason the other audit cells carry
 * a width class).
 *
 * Rather than a hand-tuned rem value that has to be revisited every time a label
 * changes, the cell carries a zero-height copy of the longest option. Sharing a
 * grid area with the controls makes the column exactly as wide as that label
 * needs, in the real font, and adding a longer decision widens it on its own.
 */
const longestDecisionLabel = (t: TFunction<'accessGrants'>) =>
  decisionOptions(t).reduce(
    (longest, option) =>
      option.label.length > longest.length ? option.label : longest,
    '',
  );

// What the sizer has to reserve beyond the label, measured off the components the
// cell composes: the trigger's px-sm (2x8) and border-2 (2x2), its chevron block
// (ml-2 8 + w-3.5 14 - mr-1 4), the cell's gap-1 (4), and the save slot (w-4 16).
const decisionSizerStyle = { paddingRight: '58px' };

// Matches the Select trigger's own type so the measurement is of the same text.
const decisionSizerClasses =
  'invisible col-start-1 row-start-1 h-0 overflow-hidden text-sm font-medium whitespace-nowrap';

// Always-visible bordered field so it reads as editable — mirrors the
// trusted-principals comment cell (tenant DetectionSettings) for a consistent
// inline-edit look and font across the app.
const cellBase =
  'w-full rounded-md border border-gray-200 bg-transparent px-2 py-1 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:placeholder:text-gray-500';
const cellTextColor = 'text-gray-700 dark:text-gray-200';
const inputClasses = `${cellBase} ${cellTextColor}`;

// A fixed-size status slot rendered next to every editable cell. It always
// occupies its 1rem square — even when idle — so the column width never shifts
// as an edit moves through saving → saved (Task 3). While a save is in flight
// it shows a spinner (Task 4); briefly after success it shows a tick.
function SaveStatus({ saving, saved }: { saving: boolean; saved: boolean }) {
  const { t } = useTranslation('accessGrants');
  return (
    <span
      className="flex h-4 w-4 shrink-0 items-center justify-center"
      aria-live="polite"
    >
      {saving ? (
        <ArrowPathIcon
          className="h-4 w-4 animate-spin text-gray-400"
          aria-label={t('saving')}
        />
      ) : saved ? (
        <CheckIcon
          className="h-4 w-4 text-emerald-500"
          aria-label={t('saved')}
        />
      ) : null}
    </span>
  );
}

/**
 * Whether this row's review columns are editable.
 *
 * AccessGrant:Review is scoped per integration, so a reviewer covering some of
 * them gets inputs only on the grants they can actually save. Checked per cell
 * rather than only on the view preset: the column picker can put these columns
 * on screen for anyone, so hiding the Access review preset is not the only way
 * in. Without the action the stored value still shows, as text.
 */
function useCanReviewGrant(grant: AccessGrant): boolean {
  const { can } = useAccessControl();
  return can({ action: 'AccessGrant:Review', resource: grant.integrationId });
}

/** The stored value as plain text, for a caller who cannot save a change. */
function ReadOnlyCell({
  value,
  widthClass = '',
}: {
  value: string | null | undefined;
  widthClass?: string;
}) {
  return (
    <div className={`flex items-center ${widthClass}`}>
      <span className="truncate text-gray-500 dark:text-gray-400">
        {value || '—'}
      </span>
    </div>
  );
}

/**
 * A borderless-until-focused text cell that saves on blur when the value
 * changed. Used for the free-text Grantee and Comment columns.
 */
function EditableTextCell({
  grant,
  field,
  initial,
  placeholder,
  widthClass,
}: {
  grant: AccessGrant;
  field: 'grantee' | 'reviewComment';
  initial: string | null;
  placeholder: string;
  widthClass: string;
}) {
  const { mutate, isPending } = useUpdateGrant();
  const { saved, flash } = useSaveIndicator();
  const [value, setValue] = useState(initial ?? '');
  const canReview = useCanReviewGrant(grant);

  const commit = () => {
    const next = value.trim();
    if (next === (initial ?? '')) {
      return;
    }
    const patch: UpdateGrantBody = { [field]: next || null };
    mutate({ id: grant.id, ...patch }, { onSuccess: flash });
  };

  if (!canReview) {
    return <ReadOnlyCell value={initial} widthClass={widthClass} />;
  }

  return (
    <div
      className={`flex items-center gap-1 ${widthClass}`}
      onClick={stopRowClick}
    >
      <input
        // The table auto-sizes columns to max-content, so an input's default
        // 20-character intrinsic width — not the wrapper's min-width — was
        // setting these columns and pushing the table past the viewport.
        // size=1 drops that intrinsic width so widthClass below actually binds.
        size={1}
        className={`${inputClasses} min-w-0`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
      />
      <SaveStatus saving={isPending} saved={saved} />
    </div>
  );
}

export function GranteeCell({ grant }: { grant: AccessGrant }) {
  const { t } = useTranslation('accessGrants');
  return (
    <EditableTextCell
      grant={grant}
      field="grantee"
      initial={grant.grantee}
      placeholder={t('addGrantee')}
      widthClass="min-w-[10rem]"
    />
  );
}

export function CommentCell({ grant }: { grant: AccessGrant }) {
  const { t } = useTranslation('accessGrants');
  return (
    <EditableTextCell
      grant={grant}
      field="reviewComment"
      initial={grant.reviewComment}
      placeholder={t('addComment')}
      widthClass="min-w-[10rem]"
    />
  );
}

export function DecisionCell({ grant }: { grant: AccessGrant }) {
  const { t } = useTranslation('accessGrants');
  const { mutate, isPending } = useUpdateGrant();
  const { saved, flash } = useSaveIndicator();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isTrustOpen, setTrustOpen] = useState(false);
  const canReview = useCanReviewGrant(grant);

  const onSelect = ({ value }: { value: string }) => {
    const reviewDecision = toDecision(value);
    // Trusting needs a date the API will accept, so this one decision breaks the
    // autosave-on-change pattern and opens the popover instead. The Select's
    // displayed value comes from the grant, so abandoning the popover leaves it
    // showing the decision that is actually stored — no revert to undo.
    //
    // Picking the option when it is ALREADY selected lands here too, which is how
    // a reviewer moves an existing window or re-pins a lapsed one: the Select
    // fires on every option click, not only on a change.
    if (reviewDecision === 'trust_until_review') {
      setTrustOpen(true);
      return;
    }
    // The popover is anchored to this cell, so a click on the Select does not
    // dismiss it — moving off trusting has to close it explicitly.
    setTrustOpen(false);
    const patch: UpdateGrantBody = { reviewDecision };
    if (isReviewLaterWithoutDate(reviewDecision, grant.nextReviewAt)) {
      patch.nextReviewAt = fromDateInput(defaultNextReviewDateInput());
    }
    // One mutation, so the decision and its default date land as a single
    // history entry rather than reading as two separate reviews.
    mutate({ id: grant.id, ...patch }, { onSuccess: flash });
  };

  const confirmTrust = (reviewDate: string) => {
    mutate(
      {
        id: grant.id,
        reviewDecision: 'trust_until_review',
        nextReviewAt: fromDateInput(reviewDate),
      },
      {
        onSuccess: () => {
          setTrustOpen(false);
          flash();
        },
      },
    );
  };

  if (!canReview) {
    return (
      <ReadOnlyCell
        value={
          decisionOptionsFor(t, grant.grantOrigin, grant.reviewDecision).find(
            (option) => option.value === grant.reviewDecision,
          )?.label
        }
      />
    );
  }

  return (
    <div ref={anchorRef} className="grid items-center" onClick={stopRowClick}>
      <span
        aria-hidden="true"
        className={decisionSizerClasses}
        style={decisionSizerStyle}
      >
        {longestDecisionLabel(t)}
      </span>
      <div className="col-start-1 row-start-1 flex items-center gap-1">
        <Select
          options={decisionOptionsFor(
            t,
            grant.grantOrigin,
            grant.reviewDecision,
          )}
          value={grant.reviewDecision ?? ''}
          onSelect={onSelect}
          // The table scrolls in both axes; portaled options escape that clipping.
          isFixedPositionOptions
          // min-w-0 is load-bearing: without it the trigger's own content sets
          // the flex item's minimum size, so a long label would push the cell
          // wider than the sizer instead of being clipped inside it. The sizer is
          // what guarantees the width; this just lets the Select fill it.
          className="min-w-0 flex-1"
          data-testid="decision-select"
        />
        <SaveStatus saving={isPending} saved={saved} />
      </div>
      {isTrustOpen && (
        <ReviewDatePopover
          onClose={() => setTrustOpen(false)}
          anchorRef={anchorRef}
          title={t('trustUntilReview')}
          description={t('fromTheNextScanThisGrantStops')}
          currentDate={grant.nextReviewAt}
          {...trustWindowBounds()}
          isSaving={isPending}
          confirmLabel={t('common:trust')}
          // Only reachable while the scan has not yet lapsed the trust. Saving
          // re-pins to the permissions the grant has now, so say so.
          warning={
            grant.trustLapseReason === 'content_changed'
              ? t('thePermissionsChangedSinceLastTrusted')
              : undefined
          }
          onConfirm={confirmTrust}
        />
      )}
    </div>
  );
}

export function NextReviewCell({ grant }: { grant: AccessGrant }) {
  const { t } = useTranslation('accessGrants');
  const { mutate, isPending } = useUpdateGrant();
  const { saved, flash } = useSaveIndicator();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const canReview = useCanReviewGrant(grant);

  const setDate = (nextReviewAt: string | null) => {
    const patch: UpdateGrantBody = { nextReviewAt };
    // Clearing the date leaves "Review later" pointing at nothing, so the
    // decision goes with it rather than sitting there meaning "never".
    if (nextReviewAt === null && grant.reviewDecision === 'review_later') {
      patch.reviewDecision = null;
    }
    mutate(
      { id: grant.id, ...patch },
      {
        onSuccess: () => {
          setOpen(false);
          flash();
        },
      },
    );
  };

  const isOverdue = isReviewOverdue(grant.nextReviewAt);
  // On a trusted grant this date IS the trust window, so it carries the API's
  // horizon. On any other decision it is a reminder with no ceiling.
  const { min, max } = trustWindowBounds();
  const isTrusting = grant.reviewDecision === 'trust_until_review';

  if (!canReview) {
    return (
      <ReadOnlyCell
        value={
          grant.nextReviewAt ? formatReviewDate(grant.nextReviewAt) : undefined
        }
      />
    );
  }

  return (
    <div
      ref={anchorRef}
      className="flex items-center gap-1"
      onClick={stopRowClick}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${cellBase} min-w-[7.5rem] text-left ${isOverdue ? 'text-red-600 dark:text-red-400' : cellTextColor}`}
      >
        {grant.nextReviewAt
          ? formatReviewDate(grant.nextReviewAt)
          : t('setDate')}
      </button>
      <SaveStatus saving={isPending} saved={saved} />
      {isOpen && (
        <ReviewDatePopover
          onClose={() => setOpen(false)}
          anchorRef={anchorRef}
          title={t('nextReview')}
          description={
            isTrusting
              ? t('thisGrantIsTrustedUntilThisDate')
              : t('whenThisGrantShouldBeLookedAt')
          }
          currentDate={grant.nextReviewAt}
          // Bounded only while trusting, where this date IS the trust window.
          // Otherwise it is a reminder, and a past one is allowed as before.
          min={isTrusting ? min : undefined}
          max={isTrusting ? max : undefined}
          isSaving={isPending}
          confirmLabel={t('common:save')}
          onClear={grant.nextReviewAt ? () => setDate(null) : undefined}
          onConfirm={(date) => setDate(fromDateInput(date))}
        />
      )}
    </div>
  );
}
